import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Circle } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { Board, BoardMember } from '../types';
import { subscribeToBoardPresence, setUserPresence, removeUserPresence, debugBoardPresence } from '../firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

interface MemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  board: Board;
}

interface ActiveUser {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  lastSeen: Date;
  isOnline: boolean;
}

const MemberModal: React.FC<MemberModalProps> = ({
  isOpen,
  onClose,
  board,
}) => {
  const { user } = useAuth();
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Subscribe to presence updates
  useEffect(() => {
    if (!isOpen || !user) return;

    console.log('MemberModal: Board members:', board.members);

    // Set current user as present
    const setPresence = async () => {
      if (user?.id && user?.email && user?.name) {
        await setUserPresence(board.id, user.id, user.email, user.name);
      }
    };
    setPresence();

    // Subscribe to presence changes
    const unsubscribe = subscribeToBoardPresence(board.id, (users) => {
      console.log('MemberModal: Received active users:', users);
      setActiveUsers(users);
      setIsLoading(false);
    });

    // Cleanup function
    return () => {
      unsubscribe();
      // Remove user presence when component unmounts
      if (user?.id) {
        removeUserPresence(board.id, user.id);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- board.members omitted to avoid unnecessary re-subscribe
  }, [isOpen, board.id, user]);

  // Cleanup presence when modal closes
  useEffect(() => {
    if (!isOpen && user?.id) {
      removeUserPresence(board.id, user.id);
    }
  }, [isOpen, board.id, user?.id]);

  // Add escape key functionality
  useEscapeKey(onClose, isOpen);

  const getActiveUser = (member: BoardMember): ActiveUser | undefined => {
    const activeUser = activeUsers.find(active => active.userEmail === member.email);
    console.log(`Looking for member ${member.email} in active users:`, activeUsers.map(u => u.userEmail), 'Found:', activeUser);
    return activeUser;
  };

  const isUserOnline = (member: BoardMember): boolean => {
    const activeUser = getActiveUser(member);
    if (!activeUser) return false;
    
    // Consider user online if they were active in the last 5 minutes
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const isOnline = activeUser.lastSeen > fiveMinutesAgo;
    console.log(`MemberModal: ${member.email} online status:`, isOnline, 'lastSeen:', activeUser.lastSeen, 'fiveMinutesAgo:', fiveMinutesAgo);
    return isOnline;
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-700';
      case 'admin':
        return 'bg-red-100 text-red-700';
      case 'editor':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-white/15 backdrop-blur-xl z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="glass-effect rounded-3xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Board Members</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Active Users Section */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
                <Circle size={12} className="text-green-500 fill-current" />
                <span>Active Now ({activeUsers.length})</span>
              </h3>
              <div className="space-y-2">
                {isLoading ? (
                  <div className="text-sm text-gray-500">Loading active users...</div>
                ) : activeUsers.length > 0 ? (
                  activeUsers.map((activeUser) => (
                    <div key={activeUser.id} className="flex items-center space-x-3 p-2 bg-green-50 rounded-lg border border-green-200">
                      <div className="w-8 h-8 bg-gradient-to-r from-satsuma-500 to-heian-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {activeUser.userName?.charAt(0).toUpperCase() || activeUser.userEmail.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {activeUser.userName || activeUser.userEmail}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {activeUser.userEmail}
                        </p>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Circle size={8} className="text-green-500 fill-current" />
                        <span className="text-xs text-green-600">Online</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-gray-500 p-2">No active users</div>
                )}
              </div>
            </div>

            {/* All Members Section */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                All Members ({board.members.length})
              </h3>
              <div className="space-y-2">
                {board.members.map((member) => {
                  const isOnline = isUserOnline(member);
                  return (
                    <div key={member.userId} className={`flex items-center space-x-3 p-2 rounded-lg border ${
                      isOnline ? 'bg-green-50 border-green-200' : 'bg-white/30 border-gray-200'
                    }`}>
                      <div className="relative">
                        <div className="w-8 h-8 bg-gradient-to-r from-satsuma-500 to-heian-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {member.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        {isOnline && (
                          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {member.email}
                        </p>
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${getRoleColor(member.role)}`}>
                            {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                          </span>
                          <span className="text-xs text-gray-500">
                            Joined {member.joinedAt ? new Date(member.joinedAt).toLocaleDateString() : 'Unknown date'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        {isOnline ? (
                          <>
                            <Circle size={8} className="text-green-500 fill-current" />
                            <span className="text-xs text-green-600">Online</span>
                          </>
                        ) : (
                          <>
                            <Circle size={8} className="text-gray-400" />
                            <span className="text-xs text-gray-500">Offline</span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center mb-3">
                Users are considered active if they've been on the board in the last 5 minutes
              </p>
              <button
                onClick={async () => {
                  console.log('Debug: Checking board presence...');
                  await debugBoardPresence(board.id);
                }}
                className="w-full px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Debug: Check Presence Data
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MemberModal;
